<template>
  <div class="waste-type-container">
    <!-- ページヘッダー -->
    <div class="page-header mb-4">
      <div class="header-content">
        <h4 class="mb-0">
          <i class="bi bi-trash me-2 text-primary"></i>ごみ種別設定
        </h4>
        <BaseButton
          variant="secondary"
          size="sm"
          @click="$emit('back-to-menu')"
        >
          <i class="bi bi-arrow-left me-2"></i>メニューに戻る
        </BaseButton>
      </div>
    </div>



    <!-- データテーブル -->
    <InfoTableSection
      title=""
      :headers="['種別名', '操作']"
      :items="wasteTypes"
      :loading="loading"
      :error="error"
      :empty-message="'登録されているごみ種別がありません'"
      :pagination="{ enabled: true, perPage: 10 }"
      @action="$emit('table-action', $event)"
      @refresh="$emit('refresh')"
    >
      <template #tableRow="{ item }">
        <BTd class="type-name">
          <ContentText color="default">{{ item.typeName }}</ContentText>
        </BTd>
        <BTd class="text-center">
          <BaseButton
            size="sm"
            variant="success"
            class="me-2"
            @click="$emit('show-edit-modal', item)"
          >
            <i class="bi bi-pencil-square me-1"></i>編集
          </BaseButton>
          <BaseButton
            size="sm"
            variant="danger"
            @click="$emit('confirm-delete', item)"
          >
            <i class="bi bi-trash me-1"></i>削除
          </BaseButton>
        </BTd>
      </template>
    </InfoTableSection>

    <!-- 新規追加ボタン -->
    <div class="add-button-section mt-3">
      <BaseButton
        variant="primary"
        size="lg"
        @click="$emit('show-add-modal')"
        :disabled="loading"
      >
        <i class="bi bi-plus-circle me-2"></i>ごみ種別を追加
      </BaseButton>
    </div>

    <!-- 追加・編集モーダル -->
    <BaseModal
      modal-id="waste-type-form-modal"
      :model-value="showModal"
      @update:model-value="$emit('close-modal')"
      :title="isEditMode ? 'ごみ種別の編集' : '新しいごみ種別の追加'"
      size="md"
      @cancel="$emit('close-modal')"
    >
      <template #default>
        <WasteTypeForm
          :model-value="formData"
          :errors="formErrors"
          :is-edit="isEditMode"
          @update:model-value="$emit('update:form-data', $event)"
          @submit="$emit('submit-form')"
        />
      </template>

      <template #footer>
        <div class="d-flex justify-content-end gap-2">
          <BaseButton
            variant="secondary"
            @click="$emit('close-modal')"
          >
            キャンセル
          </BaseButton>
          <BaseButton
            variant="primary"
            @click="$emit('submit-form')"
          >
            <i class="bi bi-check me-1"></i>
            {{ isEditMode ? '更新' : '追加' }}
          </BaseButton>
        </div>
      </template>
    </BaseModal>

    <!-- 削除確認モーダル -->
    <BaseModal
      modal-id="delete-waste-type-modal"
      :model-value="showDeleteModal"
      @update:model-value="$emit('close-delete-modal')"
      title="削除確認"
      variant="danger"
      confirm-text="削除"
      @confirm="$emit('delete-type', deleteTarget)"
      @cancel="$emit('close-delete-modal')"
    >
      <p class="mb-3">
        <strong>{{ deleteTarget?.typeName }}</strong>を削除してもよろしいですか？
      </p>
      <div class="alert alert-warning">
        <i class="bi bi-exclamation-triangle me-2"></i>
        この操作は元に戻すことができません。
      </div>
    </BaseModal>
  </div>
</template>

<script setup>
import { BTd } from 'bootstrap-vue-next'
import BaseButton from '@/components/atoms/BaseButton.vue'
import BaseModal from '@/components/molecules/BaseModal.vue'
import ContentText from '@/components/atoms/ContentText.vue'
import InfoTableSection from '@/components/organisms/InfoTableSection.vue'
import WasteTypeForm from './WasteTypeForm.vue'

const props = defineProps({
  wasteTypes: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  error: {
    type: String,
    default: null
  },
  showModal: {
    type: Boolean,
    default: false
  },
  showDeleteModal: {
    type: Boolean,
    default: false
  },
  isEditMode: {
    type: Boolean,
    default: false
  },
  formData: {
    type: Object,
    default: () => ({ typeName: '' })
  },
  formErrors: {
    type: Object,
    default: () => ({})
  },
  deleteTarget: {
    type: Object,
    default: null
  }
})

const emit = defineEmits([
  'show-add-modal',
  'show-edit-modal',
  'confirm-delete',
  'add-type',
  'edit-type',
  'delete-type',
  'close-modal',
  'close-delete-modal',
  'back-to-menu',
  'refresh',
  'submit-form',
  'table-action',
  'update:form-data'
])

</script>

<style lang="scss" scoped>
.waste-type-container {
  padding: 1.5rem 0;
}

.page-header {
  .header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-left: 2rem;
    margin-right: 2rem;
    padding-bottom: 1rem;
    border-bottom: 2px solid #e9ecef;
  }

  h4 {
    color: #1f2937;
    font-weight: 600;
    flex: 1;
    display: flex;
    align-items: center;
    margin-bottom: 0;
  }
}



.type-name {
  font-weight: 600;
  color: #374151;
}

.add-button-section {
  display: flex;
  justify-content: flex-start;
  margin-left: 2rem;
  margin-right: 2rem;
  margin-top: 1rem;
}

.alert {
  border-left: 4px solid;
  border-radius: 8px;
}

.alert-warning {
  border-left-color: var(--bs-warning);
  background-color: rgba(255, 193, 7, 0.1);
}

.text-warning {
  color: var(--bs-warning) !important;
}

/* ボタンアニメーション */
.btn-outline-primary:hover,
.btn-outline-danger:hover {
  transform: translateY(-1px);
  transition: all 0.2s ease;
}

.btn-outline-primary:hover {
  box-shadow: 0 4px 8px rgba(0, 123, 255, 0.3);
}

.btn-outline-danger:hover {
  box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
}

// 戻るボタンのスタイリング
.page-header button {
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

@media (max-width: 768px) {
  .page-header {
    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
      margin-left: 1rem;
      margin-right: 1rem;
    }

    h4 {
      order: 1; // モバイルではタイトルを上に
    }

    button {
      order: 2; // ボタンを下に
      align-self: flex-end; // 右寄せ維持
    }
  }

  .add-button-section {
    margin-left: 1rem;
    margin-right: 1rem;
  }
}
</style>
